import { IonButton, IonInput } from '@ionic/react';
import React, { KeyboardEvent, useRef, useState } from 'react';

import { ButtonContainer, Spacer } from '@/components/common';
import { Icon } from '@/components/memo';
import { useGetStarted } from '@/contexts';
import { useInputFocus, useToast } from '@/hooks';
import { MOBILE_NUMBER_LENGTH } from '@/utils/constants';
import { handleIonMobileNumberInput } from '@/utils/helpers/sanitizeMobileNumberInput';
import { arrowForwardCircleOutline } from 'ionicons/icons';

import './MobileNumber.css';

const MobileNumber: React.FC = () => {
  const { mobileNumber, setMobileNumber, sendOtp } = useGetStarted();
  const displayToast = useToast();
  const [hasError, setHasError] = useState<boolean>(false);
  const inputRef = useRef<HTMLIonInputElement>(null);

  useInputFocus(inputRef);

  const copy = {
    subHeader: 'ENTER YOUR MOBILE NUMBER',
    header: (
      <>
        What's your <span>mobile</span> number?
      </>
    ),
    message: 'An OTP will be sent to your mobile number for verification.',
    buttonText: 'Next',
    countryCode: '+63'
  };

  const onInput = (event: CustomEvent) => {
    handleIonMobileNumberInput(event, (sanitized) => {
      setMobileNumber(sanitized);
      setHasError(false);
    });
  };

  const onNext = () => {
    if (
      mobileNumber &&
      mobileNumber.length === MOBILE_NUMBER_LENGTH &&
      /^\d+$/.test(mobileNumber)
    ) {
      setHasError(false);
      sendOtp();
    } else {
      setHasError(true);
      displayToast({
        message: 'Please enter a valid mobile number.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'next-button'
      });
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      onNext();
    }
  };

  return (
    <div className="mobile-number">
      <Spacer top={32} bottom={24}>
        <h4 className="mobile-number__sub-header">{copy.subHeader}</h4>
        <h1 className="mobile-number__header">{copy.header}</h1>
        <p className="mobile-number__message">{copy.message}</p>
      </Spacer>

      <Spacer bottom={102}>
        <div className="mobile-number__form">
          <div
            className={`mobile-number__input ${hasError ? 'mobile-number__input--error' : ''}`}
          >
            <IonInput
              ref={inputRef}
              type="tel"
              label="Mobile Number"
              labelPlacement="stacked"
              value={mobileNumber}
              placeholder="9---------"
              onIonInput={onInput}
              onKeyDown={onKeyDown}
              maxlength={10}
            >
              <div slot="start">{copy.countryCode}</div>
            </IonInput>
          </div>
        </div>
      </Spacer>
      <ButtonContainer>
        <IonButton
          id="next-button"
          onClick={onNext}
          expand="block"
          fill="solid"
          size="default"
          color="primary"
          strong={true}
        >
          <Icon slot="start" icon={arrowForwardCircleOutline} />
          {copy.buttonText}
        </IonButton>
      </ButtonContainer>
    </div>
  );
};

export default MobileNumber;
